"use client";

import CreateCompanyForm from "@/components/modules/account/create-company-form";
import ModalTemplate from "@/components/molecules/modal-template";
import EmptyAddressCard from "@/components/organisms/empty-address-card";
import { useCustomer } from "@/hooks/useCustomer";
import { Locales } from "@/lib/i18n";
import { __ } from "@/lib/i18n/functions";
import { useCompanyDetails } from "@/queries/company";
import { Loader2 } from "lucide-react";
import { useState } from "react";

export type AccountCompanyDetailsProps = {
  locale: Locales;
};

const AccountCompanyDetails = ({ locale }: AccountCompanyDetailsProps) => {
  const { customer } = useCustomer();
  const [open, setIsOpen] = useState(false);
  const companyName = customer?.company_name;

  const { data: company, isLoading } = useCompanyDetails(companyName || "", customer?.id || "anonymous");

  if (isLoading || customer === undefined) {
    return (
      <div className="mt-10 flex items-center justify-center">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  return (
    <section className="space-y-5">
      <header className="flex w-full items-center justify-between">
        <h2 className="font-cabinet-grotesk text-2xl font-medium md:text-3xl">{__("Company", locale)}</h2>
        {companyName && (
          <ModalTemplate
            open={open}
            onOpenChange={setIsOpen}
            classNameTitle="font-cabinet-grotesk leading-h1 text-lg md:text-1xl"
            title={__("Edit company details", locale)}
            triggerAsChild
            modalTrigger={
              <button className="focus-visible:ring-dark-primary/70 cursor-pointer rounded-sm underline outline-none focus-visible:ring-2 focus-visible:ring-offset-2">
                {__("Edit", locale)}
              </button>
            }
          >
            <CreateCompanyForm companyToEdit={company} onSuccess={() => setIsOpen(false)} locale={locale} />
          </ModalTemplate>
        )}
      </header>
      {!companyName ? (
        <EmptyAddressCard locale={locale} variant="company" modalTitle={__("Add company details", locale)}>
          <CreateCompanyForm onSuccess={() => setIsOpen(false)} locale={locale} />
        </EmptyAddressCard>
      ) : (
        company?.name && (
          <dl className="grid gap-y-10 md:grid-cols-2 md:gap-y-12.5">
            <div className="space-y-2.5">
              {company.name && (
                <div className="flex gap-1.5">
                  <dt>{__("Company name", locale)} :</dt>
                  <dd className="font-semibold">{company.name}</dd>
                </div>
              )}
              {company.phone && (
                <div className="flex gap-1.5">
                  <dt>{__("Phone", locale)} :</dt>
                  <dd className="font-semibold">{company.phone}</dd>
                </div>
              )}
              {company.phone && (
                <div className="flex gap-1.5">
                  <dt>{__("VAT Number", locale)} : </dt>
                  <dd className="font-semibold">{company.vat}</dd>
                </div>
              )}
              {company.email && (
                <div className="flex gap-1.5">
                  <dt>{__("Email", locale)} : </dt>
                  <dd className="font-semibold">{company.email}</dd>
                </div>
              )}
            </div>
            <div className="space-y-2.5">
              {company.city && (
                <div className="flex flex-wrap gap-1.5">
                  <dt>{__("City", locale)} :</dt>
                  <dd className="font-semibold">{company.city}</dd>
                </div>
              )}
              {company.zip && (
                <div className="flex flex-wrap gap-1.5">
                  <dt>{__("Code postal", locale)} :</dt>
                  <dd className="font-semibold">{company.zip}</dd>
                </div>
              )}
              {company.address && (
                <div className="flex flex-wrap gap-1.5">
                  <dt>{__("Address", locale)} :</dt>
                  <dd className="font-semibold">{company.address}</dd>
                </div>
              )}
            </div>
          </dl>
        )
      )}
    </section>
  );
};

export default AccountCompanyDetails;
